import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import InlineMessage from '@ingka/inline-message';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import ToggleControlWrapper from '@ingka/toggle';
import FinancesByFy from '../FinancesByFy';
import FinancesTotal from '../FinancesTotal';
import useTranslation from '../../hooks/useTranslation';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { calculateAndSetTotals, getCurrencyRate } from './utils';
import { returnCurrencySymbol } from '../../utils/utils';
import { useTypedSelector } from '../../store';
import { Field, Investments } from '../FinancesByFy/FinancesByFyProps';
import { getFY, getFyNumber } from '../../utils/calculation';
import { extendFiscalYears } from '../ProjectTab/ProjectDatesFields/utils';
import countryToCurrency from 'country-to-currency/index';
import { selectCurrencyArray } from '../../store/currency/selectors';
import EditFinancesTotal from '../EditFinancesTotal';

const ProjectTabFinancials = () => {
  const t = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  // todo-ppm fix auto reset to default value in state
  const { control, getValues, setValue } = useFormContext();
  const { fields, replace } = useFieldArray({ control, name: 'investments' });
  const investment = useWatch({
    name: 'investments',
    control,
  });
  const currencies = useTypedSelector(selectCurrencyArray);
  const countryCode = useWatch({ name: 'countryCode', control });
  const currency = returnCurrencySymbol(
    countryToCurrency[countryCode] || 'SEK',
  );
  const initialStartDate = getValues('projectStartDate');
  const initialOpeningDate = getValues('projectCompletionDate');
  const initialStartDateFY = getFyNumber(getFY(0, initialStartDate));
  const initialOpeningDateFY = getFyNumber(getFY(0, initialOpeningDate));
  const startFy = getFyNumber(
    getFY(0, useWatch({ name: 'targetProjectStartDate', control })),
  );
  const endFyString = getFY(
    0,
    useWatch({ name: 'targetProjectEndDate', control }),
  );
  const endFy = getFyNumber(endFyString);
  const currencyRate = getCurrencyRate(countryCode, currencies, endFyString);
  const handleUpdateTotal = () => {
    calculateAndSetTotals(getValues('investments'), setValue);
  };

  const handleToggle = (e, index) => {
    e.preventDefault();
    setActiveIndex(index);
  };
  const isep = getValues('isep');
  const hasAnyBaseline = Array.isArray(investment)
    ? investment.some((inv) => !!inv?.BASELINE)
    : false;
  const showSplitTotals = isep || hasAnyBaseline;

  useEffect(() => {
    if (endFy - startFy > -1) {
      const targetStartFY =
        startFy > initialStartDateFY ? initialStartDateFY : startFy;
      const targetEndFY =
        endFy > initialOpeningDateFY ? endFy : initialOpeningDateFY;
      const newFields = extendFiscalYears(
        fields as unknown as Investments[],
        targetStartFY,
        targetEndFY,
      );

      replace([...newFields]);
      handleUpdateTotal();
    }
  }, [startFy, endFy]);
  return (
    <Stack pb={5} spacing={3} px={2} data-testid={'tab-financials-content'}>
      <InlineMessage
        body={t('message.inputInLocalCurrency')}
        subtle={true}
        variant="informative"
        ssrIcon={InformationCircle}
      />
      <InlineMessage
        style={{ fontWeight: 'bold' }}
        body={t('message.inputInThousands')}
        subtle={true}
        variant="negative"
        ssrIcon={InformationCircle}
      />
      <ToggleControlWrapper
        data-testid={'fys-header'}
        activeIndex={activeIndex}
        onClick={handleToggle}
        buttons={[
          {
            text: t('labels.fySplit'),
          },
          {
            text: t('common.total'),
          },
        ]}
      />
      {activeIndex === 0 && (
        <FinancesByFy
          currencies={currencies}
          countryCode={countryCode}
          handleFyItemChange={handleUpdateTotal}
          fields={(fields as Field[]) || []}
          currency={currency}
          investments={investment}
        />
      )}
      {activeIndex === 1 &&
        (showSplitTotals ? (
          <EditFinancesTotal currency={currency} currencyRate={currencyRate} />
        ) : (
          <FinancesTotal currency={currency} currencyRate={currencyRate} />
        ))}
    </Stack>
  );
};

export default ProjectTabFinancials;
